import { Injectable } from '@angular/core';
import { EvcApiService } from '@app/shared/modules/evc-api/services/evc-api.service';
import { map } from 'rxjs/operators';
import { Product } from '@app/shared/modules/evc-api/models/product';
import { ProductSetupMapperService } from './product-setup.mapper.service';
import { ChargePointLocation, ChargePointType } from './small-biz-store.model';
import { Observable } from 'rxjs';

const CHARGING_STATION_PREFIX = 'EVC_CHARGING_STATION';
const CHARGIN_STATTION_TYPE_PREFIX = `${CHARGING_STATION_PREFIX}_TYPE`;
const REGULAR_STATION_PREFIX = `${CHARGIN_STATTION_TYPE_PREFIX}_1`;
const PRO_STATION_PREFIX = `${CHARGIN_STATTION_TYPE_PREFIX}_2`;

const EXTRACT_SPEED_REGEX = 'EVC_CHARGING_STATION_TYPE_\\d+_(\\d+)';
const FILTER_PLACEMENTS_REGEX = `EVC_(PLACEMENT_)(INSIDE|OUTSIDE)_(WALL|POLE)`;
const CARD = 'EVC_CARD';

@Injectable({
  providedIn: 'root',
})
export class ProductsStoreService {
  public readonly all$ = this.api.getProducts();
  /**
   * GETTERS
   */
  public readonly codes$ = this.all$.pipe(map((products) => products.map((product) => product.code)));
  public readonly allStations$ = this.all$.pipe(
    map((products) =>
      products
        .filter(({ code }) => code.startsWith(CHARGING_STATION_PREFIX))
        .sort((a, b) => parseInt(a.price, 10) - parseInt(b.price, 10))
    )
  );
  public readonly regularStations$ = this.all$.pipe(
    map((products) =>
      products
        .filter(({ code }) => code.startsWith(REGULAR_STATION_PREFIX))
        .sort((a, b) => parseInt(a.price, 10) - parseInt(b.price, 10))
    )
  );
  public readonly proStations$ = this.all$.pipe(
    map((products) =>
      products
        .filter(({ code }) => code.startsWith(PRO_STATION_PREFIX))
        .sort((a, b) => parseInt(a.price, 10) - parseInt(b.price, 10))
    )
  );

  public readonly smartStationOption$ = this.all$.pipe(
    map((products) => products.find(({ code }) => code === 'EVC_SMART_CHARGING'))
  );

  public readonly card$ = this.all$.pipe(map((products) => products.find((product) => product.code === CARD)));

  constructor(
    private api: EvcApiService,
    private mapper: ProductSetupMapperService
  ) {}

  public getPlacementsFor(product: Product) {
    const speed = product.code.match(new RegExp(EXTRACT_SPEED_REGEX))?.[1];
    return this.all$.pipe(
      map((stations) =>
        stations
          .filter((station) => station.code.match(new RegExp(`${FILTER_PLACEMENTS_REGEX}_${speed}`)))
          .sort((a, b) => parseInt(a.price, 10) - parseInt(b.price, 10))
      )
    );
  }

  public findByCode(code: string) {
    return this.all$.pipe(map((products) => products.find((product) => product.code === code)));
  }

  public findByChargePointType(type: ChargePointType): Observable<Product> {
    const productCode = this.mapper.chargePointTypeToProductCode(type);
    return this.allStations$.pipe(map((stations) => stations.find((station) => station.code === productCode)));
  }

  public findByLocation(location: ChargePointLocation) {
    const productCode = this.mapper.locationTypeToProductCode(location);
    return this.all$.pipe(map((products) => products.find(({ code }) => code === productCode)));
  }
}
